/**
 * Labstep
 *
 * @module grid/NewIndex/coldefs/experiment-workflow/status-type
 * @desc Column Definition for experiment workflow status type
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
} from 'ag-grid-community';
import classNames from 'classnames';
import { updateExperimentWorkflowStatusType } from 'labstep-web/components/ExperimentWorkflow/Action/UpdateStatus';
import { DataGrid } from 'labstep-web/core/DataGrid';
import type {
  CellEditorParams,
  CellRendererParams,
} from 'labstep-web/core/DataGrid/types';
import { setValue } from 'labstep-web/core/DataGrid/utils';
import { DropdownPillTrigger } from 'labstep-web/core/Dropdown/Pill/Trigger';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { GridColumnDefinitionService } from 'labstep-web/grid/services/grid-column-definition.service';
import {
  type ExperimentWorkflow,
  ExperimentWorkflowStatusTypeValues,
  StatusTypes,
} from 'labstep-web/models/experiment-workflow.model';
import rules from 'labstep-web/services/validation/rules';
import { capitalize } from 'lodash';
import styles from './styles.module.scss';

export const colDefExperimentWorkflowStatus = {
  colId: 'status',
  headerName: 'Status',
  headerComponentParams: {
    icon: 'info circle',
    postFilterSettings: {
      field: 'status_type',
      disableSort: true,
    },
  },
  editable: (params: EditableCallbackParams) =>
    !params?.data?.entity_state &&
    GridNewIndexService.getEditable(params),
  valueGetter: (params: ValueGetterParams) =>
    params.data?.entity_state?.guid || params.data.statusType,
  valueGetterExport: (params: ValueGetterParams) =>
    params.node?.data?.entity_state?.name ||
    String(params.node?.data?.statusType),
  cellRenderer: (params: CellRendererParams<ExperimentWorkflow>) => {
    if (!params.data) {
      return '';
    }

    if (params.data.entity_state) {
      return (
        <DropdownPillTrigger
          triggerClassName={styles.entityStatePill}
          triggerIcon={
            StatusTypes[
              params.data.entity_state.type
                .toString()
                .toLowerCase() as keyof typeof StatusTypes
            ].icon
          }
          activeLabel={params.data.entity_state.name}
          backgroundColor={params.data.entity_state.color}
          disabled
        />
      );
    }
    return (
      <DropdownPillTrigger
        triggerClassName={classNames(
          styles.entityStatePill,
          styles[params.data.statusType],
        )}
        triggerIcon={
          StatusTypes[
            params.data.statusType as keyof typeof StatusTypes
          ].icon
        }
        activeLabel={capitalize(params.data.statusType)}
      />
    );
  },
  cellEditor: DataGrid?.CellEditor?.Select,
  cellEditorPopup: true,
  cellEditorParams: (
    params: CellEditorParams<ExperimentWorkflow>,
  ) => ({
    props: {
      value: {
        label:
          ExperimentWorkflowStatusTypeValues[
            params.data
              .statusType as keyof typeof ExperimentWorkflowStatusTypeValues
          ],
        value: params.data.statusType,
      },
      options: Object.keys(ExperimentWorkflowStatusTypeValues).map(
        (statusType) => ({
          label:
            ExperimentWorkflowStatusTypeValues[
              statusType as keyof typeof ExperimentWorkflowStatusTypeValues
            ],
          value: statusType,
        }),
      ),
      onChange: (option: {
        value: keyof typeof ExperimentWorkflowStatusTypeValues;
      }) => {
        if (!params.data) {
          return;
        }
        const body = updateExperimentWorkflowStatusType(
          params.data,
          option.value as keyof typeof ExperimentWorkflowStatusTypeValues,
        );
        if (Object.keys(body).includes('started_at')) {
          setValue(
            'started_at',
            body.started_at,
            {
              entityName: params.data.entityName,
              id: params.data.id,
            },
            rules.experiment_workflow.started_at,
            GridColumnDefinitionService.getOptions(params),
          );
        }
        if (Object.keys(body).includes('ended_at')) {
          setValue(
            'ended_at',
            body.ended_at,
            {
              entityName: params.data.entityName,
              id: params.data.id,
            },
            rules.experiment_workflow.ended_at,
            GridColumnDefinitionService.getOptions(params),
          );
        }
      },
    },
  }),
};
